import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../common/AuthContext";
import { addBrokerAccount, getBrokerId, updateBrokerAccount } from "../../../service/api/BrokerAccountApi";
import BrokerLogo from "./BrokerLogo";
import BrokerPageHeader from "./BrokerPageHeader";
import "./Brokers.css";

const configs = {
  dhan: {
    title: "Connect Dhan Account",
    subtitle: "Enter your Dhan account details",
    name: "Dhan",
    fields: [
      { key: "clientId", label: "Dhan Client ID", placeholder: "Enter your Dhan Client ID" },
      { key: "pin", label: "Dhan PIN", placeholder: "Enter your Dhan PIN", secret: true },
      { key: "totpSecret", label: "TOTP Secret", placeholder: "Enter your Dhan TOTP secret", secret: true, wide: true },
    ],
    message: "Your PIN and TOTP secret are used only to authenticate with Dhan. Credentials must be encrypted and handled securely by the server.",
  },
  fyers: {
    title: "Connect Fyers Account",
    subtitle: "Enter your Fyers API account details",
    name: "Fyers",
    fields: [
      { key: "clientId", label: "Client ID", placeholder: "Enter Fyers client ID" },
      { key: "pin", label: "PIN", placeholder: "Enter your PIN", secret: true },
      { key: "appId", label: "App ID", placeholder: "Enter app ID" },
      { key: "appSecret", label: "App Secret", placeholder: "Enter app secret", secret: true },
      { key: "appType", label: "App Type", placeholder: "Enter app type" },
      { key: "redirectUri", label: "Redirect URI", placeholder: "https://example.com/callback" },
      { key: "totpSecretKey", label: "TOTP Secret", placeholder: "Enter TOTP secret", secret: true },
    ],
    message: "Your PIN, app secret, and TOTP secret are sensitive credentials. They are sent to the server for encrypted storage and must never be logged.",
  },
  angel: {
    title: "Connect Angel One Account",
    subtitle: "Enter your Angel One SmartAPI account details",
    name: "Angel One",
    fields: [
      { key: "clientCode", label: "Client Code", placeholder: "Enter Angel One client code" },
      { key: "pin", label: "PIN", placeholder: "Enter your Angel One PIN", secret: true },
      { key: "apiKey", label: "API Key", placeholder: "Enter SmartAPI API key", secret: true },
      { key: "totpSecretKey", label: "TOTP Secret", placeholder: "Enter Angel One TOTP secret", secret: true },
    ],
    message: "Your PIN, API key, and TOTP secret are sensitive credentials. They are sent to the server for encrypted storage and must never be logged.",
  },
};

const brokerApiTypes = {
  dhan: "DHAN",
  fyers: "FYERS",
  angel: "ANGELONE",
};

const BrokerCredentials = ({ brokerType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const config = configs[brokerType];
  const editingBroker = location.state?.broker || null;
  const [form, setForm] = useState({
    accountName: editingBroker?.accountName || `My ${config.name} Account`,
    clientId: editingBroker?.clientId || "",
    clientCode: editingBroker?.clientCode || editingBroker?.clientId || "",
    appId: editingBroker?.appId || "",
    appType: editingBroker?.appType || "",
    redirectUri: editingBroker?.redirectUri || "",
  });
  const [visible, setVisible] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitting(true);
    try {
      if (!user?.userId) throw new Error("Your user session is missing. Please log in again.");

      const payload = {
        userId: user.userId,
        brokerType: brokerApiTypes[brokerType],
        accountName: form.accountName.trim(),
        active: true,
        ...(brokerType === "dhan"
          ? {
              clientId: form.clientId.trim(),
              pin: form.pin,
              totpSecret: form.totpSecret,
            }
          : brokerType === "fyers" ? {
              clientId: form.clientId.trim(),
              pin: form.pin,
              appId: form.appId.trim(),
              appSecret: form.appSecret,
              appType: form.appType.trim(),
              redirectUri: form.redirectUri.trim(),
              totpSecretKey: form.totpSecretKey,
            }
          : {
              clientCode: form.clientCode.trim(),
              pin: form.pin,
              apiKey: form.apiKey,
              totpSecretKey: form.totpSecretKey,
            }),
      };

      const brokerId = getBrokerId(editingBroker);
      const broker = brokerId
        ? await updateBrokerAccount(brokerId, payload)
        : await addBrokerAccount(payload);

      navigate("/brokers/success", {
        state: { broker: broker || { ...payload, id: brokerId } },
      });
    } catch (error) {
      setSubmitError(
        error.response?.data?.message || error.message || "Unable to connect the broker."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="broker-page">
      <BrokerPageHeader
        title={config.title}
        subtitle={config.subtitle}
        step="Step 2 of 3"
        backTo="/brokers/add"
      />

      <form className="broker-credentials-card" onSubmit={submit}>
        <div className="broker-form-intro">
          <BrokerLogo type={brokerType} />
          <div>
            <h2>{config.name} credentials</h2>
            <p>All fields are required to verify your broker account.</p>
          </div>
        </div>

        <div className="broker-form-grid">
          <label className="broker-field">
            <span>Account Name</span>
            <input name="accountName" value={form.accountName || ""} onChange={updateField} required />
          </label>

          {config.fields.map((field) => (
            <label className={`broker-field ${field.wide ? "broker-field--wide" : ""}`} key={field.key}>
              <span>{field.label}</span>
              <div className="broker-field__input">
                <input
                  name={field.key}
                  type={field.secret && !visible[field.key] ? "password" : "text"}
                  value={form[field.key] || ""}
                  placeholder={field.placeholder}
                  onChange={updateField}
                  required
                />
                {field.secret && (
                  <button
                    type="button"
                    onClick={() => setVisible((current) => ({ ...current, [field.key]: !current[field.key] }))}
                    aria-label={`Show or hide ${field.label}`}
                  >
                    <i className={`fa ${visible[field.key] ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className={`broker-security-note broker-security-note--${brokerType}`}>
          <i className="fa fa-shield" aria-hidden="true"></i>
          <span>{config.message}</span>
        </div>

        {submitError && <div className="broker-api-error">{submitError}</div>}

        <div className="broker-form-actions">
          <button type="button" className="broker-secondary-button" onClick={() => navigate("/brokers/add")}>Back</button>
          <button type="submit" className="broker-primary-button" disabled={submitting}>
            <i className="fa fa-link" aria-hidden="true"></i>
            {submitting ? "Connecting..." : "Connect & Test"}
          </button>
        </div>
      </form>
    </main>
  );
};

export default BrokerCredentials;
